import { useState, useEffect, useMemo } from 'react'
import Tooltip from '@mui/material/Tooltip'
import MenuItem from '@mui/material/MenuItem'
import Button from '../../../../components/Button'
import InlineSelect from 'components/InlineSelect'
import { useAuthContext } from 'context'
import { useTourContext } from 'context/TourContext'
import { settings } from 'settings'

const SubjectOption = ({
  option,
  startNewSubject,
  subjectOptions,
  facultyFilter,
  buttonId,
}) => {
  const { userDetails } = useAuthContext()
  const { onCloseTutorial } = useTourContext()

  const notaWidthPx = 5

  const [currentFaculty, setCurrentFaculty] = useState(
    option.value === -1 ? option.faculties?.[0]?.label : 0,
  )

  const selectedFaculty = useMemo(() => {
    if (option.value === -1) {
      const selectedFacultyLabel = option.faculties.filter(
        faculty => faculty.label === currentFaculty,
      )[0]?.value

      return subjectOptions.filter(
        subject => subject['label'] === selectedFacultyLabel,
      )[0]
    }

    return option
  }, [option, currentFaculty, subjectOptions])

  const hasMultipleMaterii = useMemo(() => {
    try {
      return isNaN(parseInt(selectedFaculty.value))
    } catch (err) {
      return true
    }
  }, [selectedFaculty])

  const [currentValue, setCurrentValue] = useState(
    hasMultipleMaterii ? Object.keys(option.value)[0] : option.value,
  )

  useEffect(() => {
    if (selectedFaculty !== undefined) {
      setCurrentValue(
        hasMultipleMaterii
          ? Object.keys(selectedFaculty.value)[0]
          : selectedFaculty.value,
      )
    }
  }, [selectedFaculty, hasMultipleMaterii])

  useEffect(() => {
    if (option.value === -1) {
      for (const faculty of option.faculties) {
        if (faculty.label === facultyFilter) {
          setCurrentFaculty(faculty.label)
          break
        }
      }
    }
  }, [facultyFilter, option])

  const userCorrectAnswersCount = useMemo(() => {
    if (option.value === -1) {
      return 0
    }
    if (hasMultipleMaterii) {
      return parseInt(option.correct_answers[currentValue])
    }

    return parseInt(option.correct_answers)
  }, [hasMultipleMaterii, option, currentValue])

  const userNota = useMemo(() => {
    if (option.value === -1) {
      return 0
    }
    return 1 + (userCorrectAnswersCount / option.count) * 9
  }, [userCorrectAnswersCount, option])

  const progressPercent = useMemo(() => {
    if (option.value === -1) {
      return 0
    }
    return (userCorrectAnswersCount / option.count) * 100
  }, [userCorrectAnswersCount, option])

  return (
    <div
      className={`bg-container-background p-2 my-[8px] rounded-md w-full flex flex-col md:flex-row gap-[20px] items-start md:items-center justify-between`}
    >
      <div className="w-full">
        <div className="flex flex-row gap-[15px] items-center justify-start">
          {option.value === -1 && option.faculties.length <= 1
            ? 'Subiect generat'
            : option.label}

          {option.value === -1 && option.faculties.length > 1 ? (
            <>
              <InlineSelect
                value={currentFaculty}
                onChange={event => setCurrentFaculty(event.target.value)}
              >
                {option.faculties.map(faculty => (
                  <MenuItem
                    key={`faculty-option-${faculty.label}`}
                    className="text-over-primary-background"
                    value={faculty.label}
                  >
                    {faculty.label}
                  </MenuItem>
                ))}
              </InlineSelect>
            </>
          ) : null}
        </div>

        {option.value !== -1 ? (
          <div
            className="w-full bg-secondary-background rounded-md h-2.5 mt-[8px]"
            style={{ position: 'relative' }}
          >
            <div
              className="bg-primary h-2.5 rounded-md"
              style={{ width: progressPercent + '%', position: 'absolute' }}
            ></div>
            {option.medie_admitere
              ? Object.entries(option.medie_admitere).map(
                  ([admitere, nota], index) => {
                    return (
                      <Tooltip
                        key={'subject-option-tooltip-' + index}
                        title={`Ultima medie ${admitere}: ${nota}`}
                      >
                        <div
                          className={`${
                            userNota >= nota ? 'bg-secondary' : 'bg-ternary'
                          } h-2.5`}
                          style={{
                            width: notaWidthPx + 'px',
                            position: 'absolute',
                            left: `calc(${nota * 10}% - ${notaWidthPx}px)`,
                          }}
                        ></div>
                      </Tooltip>
                    )
                  },
                )
              : null}
          </div>
        ) : null}

        {option.value !== -1 && (
          <div className="w-full flex justify-between text-sm mt-[4px]">
            <div>
              {selectedFaculty.time.length > 0 && <>{selectedFaculty.time}</>}
            </div>

            <div>
              {userCorrectAnswersCount || '0'}/{option.count}
            </div>
          </div>
        )}

        {selectedFaculty?.materii && (
          <div className="text-sm flex flex-row gap-[10px] items-center justify-start">
            {hasMultipleMaterii && (
              <>
                Alege {settings.materie}:{' '}
                <InlineSelect
                  value={currentValue}
                  onChange={event => setCurrentValue(event.target.value)}
                >
                  {Object.entries(selectedFaculty.value).map(
                    ([materie, id]) => (
                      <MenuItem
                        key={`materie-option-${materie}-${id}`}
                        className="text-over-primary-background"
                        value={materie}
                      >
                        {materie}
                      </MenuItem>
                    ),
                  )}
                </InlineSelect>
              </>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center justify-center">
        <Button
          className="bg-btn-primary h-10 w-24"
          onClick={async () => {
            if (!userDetails.tutorial) {
              onCloseTutorial()
            }

            if (option.value === -1) {
              const similar_to_subject = selectedFaculty

              let similar_to

              if (typeof similar_to_subject.value === 'number') {
                similar_to = similar_to_subject.value
              } else {
                similar_to = similar_to_subject.value[currentValue]
              }

              await startNewSubject(-1, undefined, similar_to)
            } else {
              if (hasMultipleMaterii) {
                await startNewSubject(option.value[currentValue])
              } else {
                await startNewSubject(currentValue)
              }
            }
          }}
          isLocked={option.isDisabled}
          isLockedMessage="Ai nevoie de un abonament pentru a rezolva acest subiect"
          id={buttonId}
        >
          Rezolvă
        </Button>
      </div>
    </div>
  )
}

export default SubjectOption
